import { DummyToRememberPeopleToCast, EntityDescriptor, EntityTablePage, EntityTablePagePartialProps, EntityTablePageReducers, EntityTablePageState, FieldDescriptor, Utils, apolloClientHolder } from "@crispico/foundation-react";
import { OverrideableElement } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { Checkbox, CheckboxProps } from "semantic-ui-react";
import { ReactElement, ReactNode } from "react";
import gql from "graphql-tag";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import { ReduxReusableComponents, RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import StringFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/StringFieldRenderer";
import DateFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/DateFieldRenderer";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { IFormValuesHolder } from "@crispico/foundation-react/entity_crud/fieldEditors/FieldEditor";
import _ from "lodash";

export enum AlertStatus { READ = 1, UNREAD = 0 };

enum AlertMessage { MODE1 = "flight.date", MODE2 = "event.delay", MODE3 = "mission.finished" };

const CLIENT_COLUMNS = ["name", "additionalMessageString"];
const ALERT_TABLE_DEFAULT_CUSTOM_COLUMN_WIDTH = 300;
const ALERT_TABLE_DEFAULT_ROW_HEIGHT = 105;
const options = FieldDescriptor.castAdditionalFieldRendererProps(StringFieldRenderer, { asLabel: true, showTooltip: true });
const optionsForDate = FieldDescriptor.castAdditionalFieldRendererProps(DateFieldRenderer, { asLabel: true, showTooltip: true, format: Utils.dateTimeFormat, formatForToday: Utils.timeFormat });

class AlertTablePageState extends EntityTablePageState {
    markAllAsRead: boolean = false;
}
class AlertTablePageReducers<S extends AlertTablePageState = AlertTablePageState> extends EntityTablePageReducers<S> { }

type AlertTablePageProps = EntityTablePagePartialProps & RRCProps<AlertTablePageState, AlertTablePageReducers>;
class AlertTablePage extends EntityTablePage<AlertTablePageProps> {

    async refresh() {
        await super.refresh();
        this.hasUnreadAlert();
    }

    protected onMarkAllAsReadChecked = () => (e: any, data: CheckboxProps) => {
        this.props.r.setInReduxState({ markAllAsRead: data.checked });
        if (data.checked) {
            this.entityTableSimpleRef.current?.getEntities().forEach(entity => (!entity.status || entity.status === AlertStatus.UNREAD) && this.markAlertAsRead(entity.id));
        }
        this.refresh();
    }

    protected hasUnreadAlert() {
        this.props.r.setInReduxState({ markAllAsRead: true });
        this.entityTableSimpleRef.current?.getEntities().every(entity => {
            if (!entity.status || entity.status === AlertStatus.UNREAD) {
                this.props.r.setInReduxState({ markAllAsRead: false });
                return false;
            }
            return true
        });
    }

    protected async markAlertAsRead(entityId: number) {
        await apolloClientHolder.apolloClient.mutate({
            mutation: gql(`mutation m($params: SaveParams_LongInput) {
                alertService_save(params: $params) { id, status }
            }`),
            variables:
                { params: { id: entityId, fieldsAndValues: { "status": AlertStatus.READ } } },
            context: { showSpinner: true }
        });
    }

    protected preRenderButtons(params: any): Array<OverrideableElement> {
        return [
            ...super.preRenderButtons(params),
            // @ts-ignore
            { elementType: Checkbox, props: { key: "markAllAsRead", disabled: this.props.markAllAsRead, checked: this.props.markAllAsRead, label: _msg("GanttMessages.markAllAsRead.label"), onChange: this.onMarkAllAsReadChecked() } },
        ];
    }

    protected getTableProps() {
        return { rowHeight: ALERT_TABLE_DEFAULT_ROW_HEIGHT };
    }

}

const AlertTablePageRRC = ReduxReusableComponents.connectRRC(AlertTablePageState, AlertTablePageReducers, AlertTablePage);

export class AlertEntityDescriptor extends EntityDescriptor {

    constructor() {
        super({
            name: "Alert",
            defaultFilter: Filter.createForClient("creationDate", FilterOperators.forDate.today),
            defaultSort: [{ field: "creationDate", direction: "DESC" }]
        });
    }

    getDefaultColumnConfig() {
        let defaultColumnConfig = _.cloneDeep(super.getDefaultColumnConfig());
        defaultColumnConfig.configObject.columns?.map((column) => {
            if (CLIENT_COLUMNS.includes(column.name)) {
                column.width = ALERT_TABLE_DEFAULT_CUSTOM_COLUMN_WIDTH;
            }
        });
        return defaultColumnConfig;
    }

    protected customize() {
        this.isInDefaultColumnConfig(true, "id", "name", "additionalMessageString", "creationDate", "status", "type", "canceled", "driverName", "subjectInfo", "message", "vehicleNo", "organization");
        this.addFieldDescriptor({ name: "creationDate", type: FieldType.date });
        this.addFieldDescriptor({ name: "status", type: FieldType.dropdown });
        this.addFieldDescriptor({ name: "additionalMessageString" }, new class extends FieldDescriptor {

            renderMode1(entity: any) {
                return <div className="flex-container">
                    <div className="very-small-padding">
                        {entityDescriptors["HumanResource"].getField("firstName").renderField(entity, options)}
                        {entityDescriptors["HumanResource"].getField("lastName").renderField(entity, options)}
                    </div>
                    <div className="very-small-padding">
                        {entityDescriptors["Flight"].getField("airline").renderField(entity, options)}
                        {entityDescriptors["Flight"].getField("number").renderField(entity, options)}
                        {entityDescriptors["Flight"].getField("date").renderField(entity, optionsForDate)}
                        {entityDescriptors["Flight"].getField("date").renderField({ date: entity.oldDate }, optionsForDate)}
                    </div>
                    <div className="very-small-padding">
                        {entityDescriptors["Task"].getField("name").renderField(entity, options)}
                    </div>
                </div>;
            }

            renderMode2(entity: any) {
                return <div className="flex-container">
                    <div className="very-small-padding">
                        {entityDescriptors["HumanResource"].getField("firstName").renderField(entity, options)}
                        {entityDescriptors["HumanResource"].getField("lastName").renderField(entity, options)}
                    </div>
                    <div className="very-small-padding">
                        {entityDescriptors["Flight"].getField("airline").renderField(entity, options)}
                        {entityDescriptors["Flight"].getField("number").renderField(entity, options)}
                        {entityDescriptors["Flight"].getField("date").renderField(entity, optionsForDate)}
                    </div>
                    <div className="very-small-padding">
                        {entityDescriptors["Task"].getField("name").renderField(entity, options)}
                        {entityDescriptors["MissionEvent2"].getField("creationdate").renderField(entity, optionsForDate)}
                    </div>
                </div>;
            }

            renderMode3(entity: any) {
                return <div className="flex-container">
                    <div className="very-small-padding">
                        {entityDescriptors["HumanResource"].getField("firstName").renderField(entity, options)}
                        {entityDescriptors["HumanResource"].getField("lastName").renderField(entity, options)}
                        {entityDescriptors["EquipmentResource"].getField("identifier").renderField(entity, optionsForDate)}
                    </div>

                    <div className="very-small-padding">
                        {entityDescriptors["Mission2"].getField("startTime").renderField(entity, optionsForDate)}
                        {entityDescriptors["Mission2"].getField("endTime").renderField(entity, optionsForDate)}
                    </div>
                </div>;
            }

            renderField(entity: any, additionalFieldRendererProps?: DummyToRememberPeopleToCast | undefined): ReactNode {
                const values = this.getFieldValue(entity) && JSON.parse(this.getFieldValue(entity))?.map;
                if (!values || !values.type) {
                    return null;
                }
                let content;
                switch (values.type) {
                    case AlertMessage.MODE1:
                        content = this.renderMode1(values);
                        break;
                    case AlertMessage.MODE2:
                        content = this.renderMode2(values);
                        break;
                    case AlertMessage.MODE3:
                        content =  this.renderMode3(values);
                        break;
                    default:
                        console.warn(`The render for ${values.type} message isn't implemented!`);
                        return null;
                }
                return <div className="Alert_additionalMessage no-wrap-no-overflow-ellipsis" >
                    {content}
                </div>
            }

            renderFieldEditor(formikProps: IFormValuesHolder<any>, additionalFieldEditorProps?: DummyToRememberPeopleToCast | undefined): ReactElement {
                return this.renderField(formikProps.values) as ReactElement;
            }

        })
    }

    renderTable() {
        return <AlertTablePageRRC {...super.renderTable().props} ref={this.entityTablePage} />;
    }
}
